import { useState } from "react";
import type { Content } from "../content/model";
import { defaultSpec, resolveView } from "./produce";
import { renderSpec } from "./render";
import type { ViewContext } from "./spec";
import { validateSpec } from "./validate";

// WHAT DREW THIS SCREEN, on the screen.
//
// Add `?spec=debug` to any unit or lesson URL. It shows which producer supplied the view, the
// spec itself, and the same content drawn twice — by the spec in force, and by the schema-derived
// default. Two panels side by side is the only way to see what an authored spec is actually
// buying, and it is where you look first when a generated one starts making choices of its own.
//
// It renders the DEFAULT with the same content and the same host slots, so the comparison is of
// the specs and nothing else.

export function SpecDebug({ ctx, content, slots }: {
  ctx: ViewContext;
  content: Content;
  slots: Record<string, React.ReactNode>;
}) {
  const [show, setShow] = useState<"rendered" | "json">("rendered");
  const resolved = resolveView(ctx);
  const fallback = defaultSpec(ctx);
  const errors = validateSpec(resolved.spec);

  return (
    <section className="sd">
      <div className="sd-bar">
        <h3>How this was drawn</h3>
        <span className={`sd-tag sd-${resolved.producer}`}>{resolved.producer}</span>
        <code className="sd-key">{resolved.spec.id}</code>
        <span className="sd-vers">
          schema v{resolved.spec.schemaVersion} · registry v{resolved.spec.registryVersion}
          {resolved.cached ? " · cached" : ""}
        </span>
        <span className="sd-spacer" />
        <button className="btn sm" onClick={() => setShow(show === "json" ? "rendered" : "json")}>
          {show === "json" ? "Show them drawn" : "Show the spec"}
        </button>
      </div>

      {/* A spec in force that does not validate is the loudest thing this page can say. */}
      {errors.length > 0 && (
        <ul className="sd-errors">
          {errors.map((e, i) => <li key={i}><code>{e.at}</code> {e.message}</li>)}
        </ul>
      )}

      {show === "json" ? (
        <pre className="sd-json">{JSON.stringify(resolved.spec, null, 2)}</pre>
      ) : (
        <div className="sd-pair">
          <div className="sd-panel">
            <h4>In force · {resolved.producer}</h4>
            <div className="sd-frame">{renderSpec(resolved.spec, content, slots)}</div>
          </div>
          <div className="sd-panel">
            <h4>Default · derived from the content schema</h4>
            {/* Every field the schema declares, in its order, whether or not anybody has decided
                how this screen should read. A field that appears HERE and not on the left is one
                the authored spec has not been told about. */}
            <div className="sd-frame">{renderSpec(fallback, content, slots)}</div>
          </div>
        </div>
      )}
    </section>
  );
}

// TURNING IT ON: `?spec=debug` on any writing URL. `?spec=off` turns it back off.
//
// It has to be remembered rather than read from the URL each time, because this app normalises
// its own URLs as you navigate — the router rewrites the path and the query is gone by the time
// anything renders. So the flag is read ONCE, as the bundle loads and while the query is still
// there, and then kept. Which also means it survives clicking from a unit to a lesson, which is
// most of what you want it for.
const KEY = "writing.spec-debug";

const asked = (() => {
  if (typeof window === "undefined") return false;
  let on = false;
  try {
    on = window.localStorage.getItem(KEY) === "1";
  } catch {
    // Private windows and blocked site data. The panel is a developer convenience; losing it is
    // not worth a thrown exception on the way to a teacher's page.
  }
  const wanted = new URLSearchParams(window.location.search).get("spec");
  if (wanted === "debug" || wanted === "off") {
    on = wanted === "debug";
    try {
      if (on) window.localStorage.setItem(KEY, "1");
      else window.localStorage.removeItem(KEY);
    } catch { /* see above */ }
  }
  return on;
})();

export function specDebugAsked(): boolean {
  return asked;
}
